import client from './client'
import type {
  Participant,
  RegistrationDetail,
  ParticipantFilters,
  PaginatedResponse,
  UpdateRegistrationPayload,
  OrganizerRefundPreview,
} from '@/types/participants'
import type { OrganizerRefund, RefundFailure } from '@/types/refunds'

export async function promoteRegistration(registrationId: number): Promise<RegistrationDetail> {
  const { data } = await client.post<{ data: RegistrationDetail }>(
    `/organizer/registrations/${registrationId}/promote`,
  )
  return data.data
}

export async function bulkPromoteRegistrations(registrationIds: number[]): Promise<RegistrationDetail[]> {
  const { data } = await client.post<{ data: RegistrationDetail[] }>(
    `/organizer/registrations/bulk-promote`,
    { registration_ids: registrationIds },
  )
  return data.data
}

function buildFilterParams(filters: ParticipantFilters): Record<string, string | number> {
  const params: Record<string, string | number> = {}
  if (filters.status) params.status = filters.status
  if (filters.search) params.search = filters.search
  if (filters.per_page) params.per_page = filters.per_page
  if (filters.page) params.page = filters.page
  return params
}

export async function getEditionParticipants(
  editionId: number,
  filters: ParticipantFilters = {},
): Promise<PaginatedResponse<Participant>> {
  const { data } = await client.get<PaginatedResponse<Participant>>(
    `/organizer/editions/${editionId}/participants`,
    { params: buildFilterParams(filters) },
  )
  return data
}

export async function getRaceParticipants(
  raceId: number,
  filters: ParticipantFilters = {},
): Promise<PaginatedResponse<Participant>> {
  const { data } = await client.get<PaginatedResponse<Participant>>(
    `/organizer/races/${raceId}/participants`,
    { params: buildFilterParams(filters) },
  )
  return data
}

export async function getRegistration(registrationId: number): Promise<RegistrationDetail> {
  const { data } = await client.get<{ data: RegistrationDetail }>(
    `/organizer/registrations/${registrationId}`,
  )
  return data.data
}

export async function updateRegistration(
  registrationId: number,
  payload: UpdateRegistrationPayload,
): Promise<RegistrationDetail> {
  const { data } = await client.put<{ data: RegistrationDetail }>(
    `/organizer/registrations/${registrationId}`,
    payload,
  )
  return data.data
}

export async function getOrganizerRefundPreview(
  registrationId: number,
): Promise<OrganizerRefundPreview> {
  const { data } = await client.get<{ data: OrganizerRefundPreview }>(
    `/organizer/registrations/${registrationId}/refund-preview`,
  )
  return data.data
}

export interface CancelRegistrationResult {
  registration: RegistrationDetail
  /** Non-null when the cancellation took effect but Stripe rejected the refund. */
  refundFailure: RefundFailure | null
}

export async function cancelRegistration(
  registrationId: number,
  refundAmount: number | null,
): Promise<CancelRegistrationResult> {
  const { data } = await client.post<{
    data: RegistrationDetail
    meta?: { refund_failure?: RefundFailure | null }
  }>(`/organizer/registrations/${registrationId}/cancel`, { refund_amount: refundAmount })
  return { registration: data.data, refundFailure: data.meta?.refund_failure ?? null }
}

export interface IssueRefundPayload {
  /** Always a 2-decimal string, e.g. "12.50". */
  amount: string
  reason: string | null
}

export interface IssueRefundApiResult {
  refund: OrganizerRefund
  /** True on HTTP 202: the provider outcome is unknown and the refund shows as Processing. */
  unconfirmed: boolean
}

export async function issueRegistrationRefund(
  registrationId: number,
  payload: IssueRefundPayload,
): Promise<IssueRefundApiResult> {
  const response = await client.post<{ data: OrganizerRefund }>(
    `/organizer/registrations/${registrationId}/refunds`,
    payload,
  )
  return { refund: response.data.data, unconfirmed: response.status === 202 }
}

export async function resendConfirmationEmail(registrationId: number): Promise<void> {
  await client.post(`/organizer/registrations/${registrationId}/resend-confirmation`)
}

const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '')

export function getEditionParticipantsExportUrl(
  editionId: number,
  filters: ParticipantFilters = {},
): string {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.search) params.set('search', filters.search)
  const queryString = params.toString()
  return `${apiBase}/organizer/editions/${editionId}/participants/export${queryString ? `?${queryString}` : ''}`
}

export function getRaceParticipantsExportUrl(
  raceId: number,
  filters: ParticipantFilters = {},
): string {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.search) params.set('search', filters.search)
  const queryString = params.toString()
  return `${apiBase}/organizer/races/${raceId}/participants/export${queryString ? `?${queryString}` : ''}`
}
